// The building blocks of a docs page: headings with an anchor (the "On this page" index is read from them), code blocks
// with highlighting and Copy, parameter tables, notes, an endpoint line. Pages use only these, so every page looks alike.
import { Children, isValidElement, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Info, Lightbulb, Link2 } from 'lucide-react'
import { CopyButton } from '@/kit/ui'

/** "ctx.lender(call)" → "ctx-lender-call" */
export const slugOf = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
const textOf = (node: ReactNode): string => Children.toArray(node).map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : isValidElement<{ children?: ReactNode }>(c) ? textOf(c.props.children) : '')).join('')

function Heading({ level, id, children }: { level: 2 | 3; id?: string; children: ReactNode }) {
  const anchor = id ?? slugOf(textOf(children))
  const Tag = level === 2 ? 'h2' : 'h3'
  return (
    <Tag id={anchor} data-toc={level} className={`group scroll-mt-20 font-bold text-default ${level === 2 ? 'mb-3 mt-10 border-b border-slate-200 pb-2 text-xl dark:border-slate-700' : 'mb-2 mt-7 text-base'}`}>
      {children}
      <a href={`#${anchor}`} className="ml-2 inline-flex align-middle text-muted opacity-0 transition group-hover:opacity-100" aria-label={`Link to ${textOf(children)}`}><Link2 size={14} /></a>
    </Tag>
  )
}
export const H2 = (p: { id?: string; children: ReactNode }) => <Heading level={2} {...p} />
export const H3 = (p: { id?: string; children: ReactNode }) => <Heading level={3} {...p} />

export function Lead({ children }: { children: ReactNode }) {
  return <p className="mb-4 text-[15px] leading-relaxed text-slate-600 dark:text-slate-300">{children}</p>
}
export function P({ children }: { children: ReactNode }) {
  return <p className="my-3 leading-relaxed">{children}</p>
}
export function List({ children, ordered = false }: { children: ReactNode; ordered?: boolean }) {
  const Tag = ordered ? 'ol' : 'ul'
  return <Tag className={`my-3 space-y-1.5 pl-6 leading-relaxed ${ordered ? 'list-decimal' : 'list-disc'}`}>{children}</Tag>
}
/** inline code */
export function C({ children }: { children: ReactNode }) {
  return <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.86em] text-rose-700 dark:bg-slate-800 dark:text-rose-300">{children}</code>
}
/** a link to another docs page (and heading) */
export function DocLink({ to, children }: { to: string; children: ReactNode }) {
  return <Link to={`/docs/${to}`} className="font-medium">{children}</Link>
}

const NOTE = {
  info: { Icon: Info, cls: 'border-sky-300 bg-sky-50 dark:border-sky-800 dark:bg-sky-950/40', title: 'Note' },
  tip: { Icon: Lightbulb, cls: 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40', title: 'Tip' },
  warning: { Icon: AlertTriangle, cls: 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40', title: 'Important' },
}
export function Note({ kind = 'info', title, children }: { kind?: keyof typeof NOTE; title?: string; children: ReactNode }) {
  const n = NOTE[kind]
  return (
    <div className={`my-4 flex gap-3 rounded-md border-l-4 px-4 py-3 ${n.cls}`} role="note">
      <n.Icon size={17} className="mt-0.5 shrink-0" />
      <div className="min-w-0 leading-relaxed"><b>{title ?? n.title}.</b> {children}</div>
    </div>
  )
}

/** A table: the first column in code font when `code` is set. */
export function Table({ head, rows, code = true }: { head: string[]; rows: ReactNode[][]; code?: boolean }) {
  return (
    <div className="my-4 overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700">
      <table className="w-full border-collapse text-[13px]">
        <thead className="bg-slate-50 dark:bg-slate-900"><tr>{head.map((h) => <th key={h} className="border-b border-slate-200 px-3 py-2 text-left font-semibold dark:border-slate-700">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0 dark:border-slate-800">
              {r.map((c, j) => <td key={j} className={`px-3 py-2 align-top leading-relaxed ${j === 0 && code ? 'whitespace-nowrap font-mono text-[12.5px] text-violet-700 dark:text-violet-300' : ''}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Parameters / fields: name, type, required, what. */
export function Params({ rows }: { rows: [name: string, type: string, required: boolean | string, what: ReactNode][] }) {
  return <Table head={['Name', 'Type', 'Required', 'Description']} rows={rows.map(([n, t, r, w]) => [n, <span className="font-mono text-[12px] text-sky-700 dark:text-sky-300">{t}</span>, typeof r === 'string' ? r : r ? 'yes' : 'no', w])} />
}

const METHOD = { GET: 'bg-emerald-600', POST: 'bg-sky-600', PUT: 'bg-amber-600', PATCH: 'bg-violet-600', DELETE: 'bg-red-600' } as const
export function Endpoint({ method, path, note }: { method: keyof typeof METHOD; path: string; note?: string }) {
  return (
    <div className="my-3 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-[13px] dark:border-slate-700 dark:bg-slate-900">
      <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold text-white ${METHOD[method]}`}>{method}</span>
      <span>{path}</span>
      {note && <span className="font-sans text-2xs text-muted">{note}</span>}
    </div>
  )
}

// ---- code with highlighting -------------------------------------------------------------------------------------

const KEYWORDS = new Set(['const', 'let', 'var', 'function', 'async', 'await', 'return', 'if', 'else', 'for', 'of', 'in', 'while', 'new', 'throw', 'try', 'catch', 'finally', 'require', 'module', 'exports', 'true', 'false', 'null', 'undefined', 'typeof', 'class', 'export', 'default', 'import', 'from', 'this'])
const TOKEN = /(\/\/[^\n]*|\/\*[\s\S]*?\*\/|#[^\n]*)|('(?:\\.|[^'\\\n])*'|"(?:\\.|[^"\\\n])*"|`(?:\\.|[^`\\])*`)|(\b\d+(?:\.\d+)?\b)|(\b[A-Za-z_$][\w$]*\b)/g
type Lang = 'js' | 'json' | 'bash' | 'text'

function highlight(code: string, lang: Lang): ReactNode[] {
  if (lang === 'text') return [code]
  const out: ReactNode[] = []
  let at = 0
  for (const m of code.matchAll(TOKEN)) {
    const [whole, comment, str, num, word] = m
    const i = m.index ?? 0
    if (i > at) out.push(code.slice(at, i))
    at = i + whole.length
    if (comment && (lang === 'bash' ? comment.startsWith('#') : !comment.startsWith('#'))) out.push(<span key={i} className="text-slate-400 italic">{comment}</span>)
    else if (comment) out.push(comment)
    else if (str) out.push(<span key={i} className={lang === 'json' && /^\s*:/.test(code.slice(at)) ? 'text-sky-700 dark:text-sky-300' : 'text-emerald-700 dark:text-emerald-400'}>{str}</span>)
    else if (num) out.push(<span key={i} className="text-amber-700 dark:text-amber-400">{num}</span>)
    else if (word && lang === 'js' && KEYWORDS.has(word)) out.push(<span key={i} className="text-violet-700 dark:text-violet-400">{word}</span>)
    else if (word && lang === 'js' && /^\s*\(/.test(code.slice(at))) out.push(<span key={i} className="text-blue-700 dark:text-blue-400">{word}</span>)
    else if (word && lang === 'json' && ['true', 'false', 'null'].includes(word)) out.push(<span key={i} className="text-violet-700 dark:text-violet-400">{word}</span>)
    else out.push(whole)
  }
  if (at < code.length) out.push(code.slice(at))
  return out
}

/** A code block: an optional file name / title, highlighting, Copy. */
export function Code({ children, lang = 'js', title }: { children: string; lang?: Lang; title?: string }) {
  const code = children.replace(/^\n/, '').replace(/\s+$/, '')
  return (
    <div className="my-4 overflow-hidden rounded-md border border-slate-200 dark:border-slate-700">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-100 px-3 py-1 dark:border-slate-700 dark:bg-slate-800">
        <span className="truncate font-mono text-2xs text-muted">{title ?? { js: 'JavaScript', json: 'JSON', bash: 'Terminal', text: 'Text' }[lang]}</span>
        <CopyButton text={code} className="!min-h-0 py-0.5" />
      </div>
      <pre className="overflow-x-auto bg-slate-50 p-4 font-mono text-[12.5px] leading-relaxed dark:bg-slate-900"><code>{highlight(code, lang)}</code></pre>
    </div>
  )
}

/** Numbered steps, each with a title and text. */
export function Steps({ items }: { items: [title: string, body: ReactNode][] }) {
  return (
    <ol className="my-4 space-y-3">
      {items.map(([t, b], i) => (
        <li key={t} className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-2xs font-bold text-white">{i + 1}</span>
          <div className="min-w-0 pt-0.5 leading-relaxed"><b>{t}</b><div>{b}</div></div>
        </li>
      ))}
    </ol>
  )
}

/** Boxes with arrows between them: how a call travels. */
export function Flow({ items }: { items: [title: string, sub: string][] }) {
  return (
    <div className="my-4 flex flex-wrap items-stretch gap-2">
      {items.map(([t, s], i) => (
        <div key={t} className="flex items-center gap-2">
          <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-center dark:border-slate-700 dark:bg-slate-900">
            <div className="text-xs font-semibold">{t}</div>
            <div className="text-2xs text-muted">{s}</div>
          </div>
          {i < items.length - 1 && <span className="text-muted" aria-hidden="true">→</span>}
        </div>
      ))}
    </div>
  )
}
